import { useEffect, useMemo, useRef, useState } from 'react';

import {
  MAX_HOLD_MS,
  SETTLE_GRACE_MS,
  WATCHDOG_RECHECK_MS
} from '../../frozenDrop.constants';

export interface ColumnLoadingOperation {
  id: string;
  /**
   * False once a newer drag has started — this operation no longer owns the
   * shared drag-preview state and must not tear it down.
   */
  ownsDragPreview: () => boolean;
}

interface UseColumnLoadingOptions {
  /** True while anything is in flight for this view (mutation or refetch). */
  isLoading: boolean;
  /**
   * True while a dialog is waiting on the user. The operation isn't finished
   * yet, so the watchdog holds off instead of dropping the spinner mid-decision.
   */
  holdWhileOpen?: boolean;
  /**
   * Identifies the board the columns belong to — `usePlanning`'s `boardKey`.
   * An operation only speaks for the board it was started on: once this changes
   * its columns drop out of `columnIds`, because the load in progress is now the
   * new board arriving, not that operation. Without it the per-column overlays
   * would keep standing in for the global one while a whole new board loads, and
   * would paint columns that no longer exist. The operation stays tracked, so it
   * still releases normally.
   */
  boardKey?: string;
  /**
   * The board data itself (`planningEntities`). A new identity means fresh data
   * was written, which is how a released operation tells its own result has
   * landed without waiting for the whole view to fall idle — see `release`.
   */
  boardData?: unknown;
}

interface UseColumnLoadingReturn {
  /** De-duplicated columns with an in-flight column-scoped operation. */
  columnIds: ReadonlySet<string>;
  /** How many in-flight operations affect the board as a whole. */
  boardOperationCount: number;
  /** Call on drag start: invalidates `ownsDragPreview` for older operations. */
  beginDrag: () => void;
  start: (columnIds: string[]) => ColumnLoadingOperation;
  /**
   * Registers an operation that reloads the board rather than named columns
   * (connect, a context-menu unassign). Its load belongs to the global overlay,
   * not to per-column spinners.
   */
  startBoardWide: () => ColumnLoadingOperation;
  release: (operationId: string) => void;
}

interface Operation {
  boardKey: string;
  columnIds: string[];
  isBoardWide: boolean;
}

/**
 * Tracks in-flight drag mutations per operation, so only the columns a
 * mutation touches show a loading state and the rest of the board stays usable.
 *
 * Releases are deferred until the operation's result has landed rather than
 * applied at the mutation response, so the per-column overlays cover the whole
 * operation (response *and* refetch) without the global overlay flashing in
 * between.
 */
export const useColumnLoading = ({
  isLoading,
  holdWhileOpen = false,
  boardKey = '',
  boardData
}: UseColumnLoadingOptions): UseColumnLoadingReturn => {
  const [operations, setOperations] = useState<Record<string, Operation>>({});

  const operationIdRef = useRef(0);
  const dragGenerationRef = useRef(0);

  // Operations whose terminal callback has fired, against the board-data version
  // they saw at that moment. Watchdogs double as the registry of live operations.
  const pendingReleaseRef = useRef(new Map<string, number>());
  const watchdogsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const flushTimeoutsRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>()
  );

  // Latest committed values, for the timers to read.
  const isLoadingRef = useRef(isLoading);
  const holdWhileOpenRef = useRef(holdWhileOpen);
  // Read by `start`, which runs from a drop handler rather than a render.
  const boardKeyRef = useRef(boardKey);
  const boardDataVersionRef = useRef(0);

  // A Set so consumers can do O(1) membership checks (`.has`). The one thing
  // here worth memoizing: it is handed to the memoized column components, which
  // would otherwise re-render on every render of the view. The React Compiler
  // runs in annotation mode (see `vite.config.mts`), and this hook does not opt
  // in, so nothing else memoizes this.
  const columnIds = useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        Object.values(operations)
          .filter((operation) => operation.boardKey === boardKey)
          .flatMap(({ columnIds: operationColumnIds }) => operationColumnIds)
      ),
    [boardKey, operations]
  );

  const boardOperationCount = Object.values(operations).filter(
    (operation) => operation.isBoardWide && operation.boardKey === boardKey
  ).length;

  const remove = (operationIds: string[]) => {
    operationIds.forEach((operationId) => {
      pendingReleaseRef.current.delete(operationId);
      const watchdog = watchdogsRef.current.get(operationId);
      if (watchdog !== undefined) {
        clearTimeout(watchdog);
        watchdogsRef.current.delete(operationId);
      }
      const flush = flushTimeoutsRef.current.get(operationId);
      if (flush !== undefined) {
        clearTimeout(flush);
        flushTimeoutsRef.current.delete(operationId);
      }
    });

    setOperations((previous) => {
      if (!operationIds.some((operationId) => operationId in previous)) {
        return previous;
      }
      const next = { ...previous };
      operationIds.forEach((operationId) => delete next[operationId]);
      return next;
    });
  };

  // One timer per operation, so each release is decided on its own terms. A
  // shared timer gated on the view-global `isLoading` made concurrent drags wait
  // for each other: the column whose mutation finished first kept its spinner —
  // and stayed un-droppable — until every other operation had settled too.
  const scheduleFlush = (operationId: string) => {
    const flushTick = () => {
      flushTimeoutsRef.current.delete(operationId);

      const versionAtRelease = pendingReleaseRef.current.get(operationId);
      if (versionAtRelease === undefined) return;

      // Nothing left to wait for. On a board with one operation this is the
      // only way out, which keeps the column overlays clearing on the same
      // frame as the frozen preview (`useFrozenDrop` waits for the same edge).
      if (!isLoadingRef.current) {
        remove([operationId]);
        return;
      }

      // Still loading, but another operation is live and this one's own result
      // has landed — the board was written after it reported back, which is
      // either the response's inline entities or the refetch it asked for. The
      // residual loading belongs to the other operation, so stop holding this
      // one's columns for it. (A write driven by that other operation can
      // satisfy this too; the cost is clearing on a board that was just
      // refreshed anyway, and only ever while a second drag is in flight.)
      const hasOtherLiveOperation = [...watchdogsRef.current.keys()].some(
        (liveId) => liveId !== operationId
      );
      if (
        hasOtherLiveOperation &&
        boardDataVersionRef.current !== versionAtRelease
      ) {
        remove([operationId]);
        return;
      }

      // Look again shortly rather than waiting for the view to fall idle.
      flushTimeoutsRef.current.set(
        operationId,
        setTimeout(flushTick, SETTLE_GRACE_MS)
      );
    };

    const existing = flushTimeoutsRef.current.get(operationId);
    if (existing !== undefined) clearTimeout(existing);
    flushTimeoutsRef.current.set(
      operationId,
      setTimeout(flushTick, SETTLE_GRACE_MS)
    );
  };

  const beginDrag = () => {
    dragGenerationRef.current += 1;
  };

  const startOperation = (
    operation: Omit<Operation, 'boardKey'>
  ): ColumnLoadingOperation => {
    operationIdRef.current += 1;
    const id = String(operationIdRef.current);
    // Captured here rather than in a ref, so `ownsDragPreview()` keeps
    // answering correctly after the operation has been released.
    const generation = dragGenerationRef.current;

    // Look again shortly while a dialog is waiting on the user, so recovery
    // follows the hold rather than waiting out another full cap. Re-armed into
    // the same map entry, which doubles as the registry of live operations.
    const watchdogTick = () => {
      if (holdWhileOpenRef.current) {
        watchdogsRef.current.set(
          id,
          setTimeout(watchdogTick, WATCHDOG_RECHECK_MS)
        );
        return;
      }
      remove([id]);
    };
    watchdogsRef.current.set(id, setTimeout(watchdogTick, MAX_HOLD_MS));
    setOperations((previous) => ({
      ...previous,
      [id]: { ...operation, boardKey: boardKeyRef.current }
    }));

    return {
      id,
      ownsDragPreview: () => dragGenerationRef.current === generation
    };
  };

  const start = (operationColumnIds: string[]) =>
    startOperation({ columnIds: operationColumnIds, isBoardWide: false });

  const startBoardWide = () =>
    startOperation({ columnIds: [], isBoardWide: true });

  const release = (operationId: string) => {
    // Unknown, already released, or already pending: a successful mutation
    // reports through both `onSuccess` and `onComplete`.
    if (!watchdogsRef.current.has(operationId)) return;
    if (pendingReleaseRef.current.has(operationId)) return;

    pendingReleaseRef.current.set(operationId, boardDataVersionRef.current);
    scheduleFlush(operationId);
  };

  useEffect(() => {
    holdWhileOpenRef.current = holdWhileOpen;
  }, [holdWhileOpen]);

  useEffect(() => {
    boardKeyRef.current = boardKey;
  }, [boardKey]);

  useEffect(() => {
    boardDataVersionRef.current += 1;
  }, [boardData]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    const watchdogs = watchdogsRef.current;
    const flushTimeouts = flushTimeoutsRef.current;
    const pendingRelease = pendingReleaseRef.current;
    return () => {
      watchdogs.forEach((watchdog) => clearTimeout(watchdog));
      watchdogs.clear();
      flushTimeouts.forEach((flush) => clearTimeout(flush));
      flushTimeouts.clear();
      pendingRelease.clear();
    };
  }, []);

  return {
    columnIds,
    boardOperationCount,
    beginDrag,
    start,
    startBoardWide,
    release
  };
};
